'use client'

import { useState, useEffect } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { useRouter, useSearchParams } from 'next/navigation'
import { useTranslation } from 'react-i18next'
import { Button, Input, Card, CardContent, CardDescription, CardHeader, CardTitle } from '@planner/ui'
import { safeInternalNext } from '@/lib/safe-next'
import { authFetch } from '@/lib/auth-fetch'
import { supabase } from '@/lib/supabase'

export function RegisterForm() {
  const { signUp } = useAuth()
  const router = useRouter()
  const searchParams = useSearchParams()
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [fullName, setFullName] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [mounted, setMounted] = useState(false)

  // Prevent hydration issues with useSearchParams
  useEffect(() => {
    setMounted(true)
  }, [])

  // Safe way to get search params
  const getSearchParam = (key: string): string | null => {
    if (typeof window === 'undefined') return null
    if (!mounted) {
      // Fallback to window.location during SSR/hydration
      const params = new URLSearchParams(window.location.search)
      return params.get(key)
    }
    return searchParams.get(key)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)

    const { error, data } = await signUp(email, password, fullName)

    if (error) {
      setError(error.message)
      setLoading(false)
    } else {
      // Always register with free plan, but check for pendingPlan in localStorage
      // This allows users to select a paid plan on landing page but register with free first
      try {
        // Wait for session to be available - check multiple times
        let session = data?.session
        let attempts = 0
        const maxAttempts = 10
        
        while (!session && attempts < maxAttempts) {
          await new Promise(resolve => setTimeout(resolve, 500))
          const { data: sessionData } = await supabase.auth.getSession()
          session = sessionData.session
          attempts++
        }

        if (!session) {
          // If no session after waiting, redirect to confirm-email page anyway
          // User will need to verify email first
          const pendingPlan = typeof window !== 'undefined' ? localStorage.getItem('pendingPlan') : null
          if (pendingPlan) {
            router.replace(`/register/confirm-email?plan=${encodeURIComponent(pendingPlan)}`)
          } else {
            router.replace('/register/confirm-email')
          }
          router.refresh()
          return
        }
        
        // Initialize company for new user (always with free plan)
        const response = await authFetch('/api/user/initialize-company', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        })

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ error: 'Failed to initialize company' }))
          // If 401, session might not be ready yet - redirect to confirm-email anyway
          if (response.status === 401) {
            console.warn('Session not ready for company initialization')
            const pendingPlan = typeof window !== 'undefined' ? localStorage.getItem('pendingPlan') : null
            if (pendingPlan) {
              router.replace(`/register/confirm-email?plan=${encodeURIComponent(pendingPlan)}`)
            } else {
              router.replace('/register/confirm-email')
            }
            router.refresh()
            return
          }
          throw new Error(errorData.error || 'Failed to initialize company')
        }

        // Wait for role to be updated in database
        await new Promise(resolve => setTimeout(resolve, 500))
        // Refresh router to trigger AuthContext to refetch role
        router.refresh()
        // Small additional delay before redirect to ensure role is refreshed
        await new Promise(resolve => setTimeout(resolve, 300))

        // Check if there's a pending plan from landing page
        const pendingPlan = typeof window !== 'undefined' ? localStorage.getItem('pendingPlan') : null
        
        // Always redirect to confirm-email page after registration
        // User needs to verify email before proceeding to payment
        if (pendingPlan) {
          router.replace(`/register/confirm-email?plan=${encodeURIComponent(pendingPlan)}`)
        } else {
          router.replace('/register/confirm-email')
        }
        router.refresh()
      } catch (err) {
        console.error('Error initializing company:', err)
        // Even if initialization fails, redirect to confirm-email
        const pendingPlan = typeof window !== 'undefined' ? localStorage.getItem('pendingPlan') : null
        if (pendingPlan) {
          router.replace(`/register/confirm-email?plan=${encodeURIComponent(pendingPlan)}`)
        } else {
          router.replace('/register/confirm-email')
        }
        router.refresh()
      }
    }
  }

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader>
        <CardTitle>{t('auth.registerTitle')}</CardTitle>
        <CardDescription>
          Utwórz nowe konto
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <label htmlFor="fullName" className="text-sm font-medium">
              Imię i nazwisko
            </label>
            <Input
              id="fullName"
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <label htmlFor="email" className="text-sm font-medium">
              {t('auth.email')}
            </label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="email@example.com"
            />
          </div>
          <div className="space-y-2">
            <label htmlFor="password" className="text-sm font-medium">
              {t('auth.password')}
            </label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
            />
          </div>
          {error && (
            <div className="text-sm text-destructive">{error}</div>
          )}
          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? t('common.loading') : t('auth.registerTitle')}
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}

